import React, { useRef, useEffect, forwardRef } from 'react';
import { useSubscription } from '../../contexts/SubscriptionContext';

const MyListsHeader = forwardRef(function MyListsHeader({
  user,
  isScrolled,
  isReorderMode,
  isCompactView,
  showAddMenu,
  canReorderLists,
  listCount,
  canAccessCollections,
  onProfileClick,
  onToggleCompactView,
  onToggleReorderMode,
  onToggleAddMenu,
  onNewList,
  onNewCollection,
  onAddPlace,
  reorderBtnRef,
}, ref) {
  const { isPremium } = useSubscription();
  const addMenuRef = useRef(null);

  // Close add menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (event.target.closest('.tutorial-overlay') || event.target.closest('.tutorial-tooltip')) {
        return;
      }
      if (addMenuRef.current && !addMenuRef.current.contains(event.target)) {
        onToggleAddMenu(false);
      }
    };

    if (showAddMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showAddMenu, onToggleAddMenu]);

  return (
    <div ref={ref} className={`section-header ${isScrolled ? 'scrolled' : ''}`}>
      <button
        className="profile-toggle-btn"
        onClick={() => !isReorderMode && onProfileClick()}
        disabled={isReorderMode}
        aria-label="Open profile"
      >
        <div className={`profile-avatar-small ${isPremium ? 'premium-avatar' : ''}`}>
          {(user?.profilePicture || user?.photoURL) ? (
            <img src={user.profilePicture || user.photoURL} alt="Profile" className="profile-photo" />
          ) : (
            <span className="profile-initial">
              {(user?.displayName || user?.name || user?.username || 'U').charAt(0).toUpperCase()}
            </span>
          )}
        </div>
      </button>
      <div className="header-actions">
        {/* Compact View Toggle */}
        <button
          className={`btn-compact-view ${isCompactView ? 'active' : ''}`}
          onClick={onToggleCompactView}
          disabled={isReorderMode}
          aria-label={isCompactView ? 'Show full cards' : 'Show compact cards'}
          title={isCompactView ? 'Show full cards' : 'Show compact cards'}
        >
          {isCompactView ? (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <rect x="3" y="3" width="7" height="7" rx="1"/>
              <rect x="14" y="3" width="7" height="7" rx="1"/>
              <rect x="3" y="14" width="7" height="7" rx="1"/>
              <rect x="14" y="14" width="7" height="7" rx="1"/>
            </svg>
          ) : (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <line x1="3" y1="6" x2="21" y2="6"/>
              <line x1="3" y1="12" x2="21" y2="12"/>
              <line x1="3" y1="18" x2="21" y2="18"/>
            </svg>
          )}
        </button>
        {canReorderLists && listCount > 1 && (
          <button
            ref={reorderBtnRef}
            className={`btn-reorder ${isReorderMode ? 'active' : ''}`}
            onClick={onToggleReorderMode}
            aria-label={isReorderMode ? 'Done reordering' : 'Reorder lists'}
          >
            {isReorderMode ? (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <polyline points="20 6 9 17 4 12"/>
              </svg>
            ) : (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <line x1="4" y1="6" x2="20" y2="6"/>
                <line x1="4" y1="12" x2="20" y2="12"/>
                <line x1="4" y1="18" x2="20" y2="18"/>
                <circle cx="8" cy="6" r="2" fill="black" strokeWidth="2"/>
                <circle cx="16" cy="12" r="2" fill="black" strokeWidth="2"/>
                <circle cx="10" cy="18" r="2" fill="black" strokeWidth="2"/>
              </svg>
            )}
          </button>
        )}
        <div className="add-menu-container" ref={addMenuRef}>
          <button
            className={`btn-new-list ${showAddMenu ? 'active' : ''}`}
            onClick={() => !isReorderMode && onToggleAddMenu(!showAddMenu)}
            disabled={isReorderMode}
            aria-label="Add new"
            aria-expanded={showAddMenu}
            aria-haspopup="menu"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
          </button>
          {showAddMenu && (
            <div className="add-menu-dropdown" role="menu">
              <button className="add-menu-item" onClick={onNewList} role="menuitem">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                  <polyline points="14 2 14 8 20 8"/>
                  <line x1="12" y1="18" x2="12" y2="12"/>
                  <line x1="9" y1="15" x2="15" y2="15"/>
                </svg>
                <span>New List</span>
              </button>
              {canAccessCollections && (
                <button className="add-menu-item" onClick={onNewCollection} role="menuitem">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                    <rect x="2" y="7" width="20" height="14" rx="2" ry="2"/>
                    <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/>
                  </svg>
                  <span>New Collection</span>
                </button>
              )}
              <button
                className="add-menu-item"
                onClick={onAddPlace}
                role="menuitem"
                disabled={listCount === 0}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                  <circle cx="12" cy="10" r="3"/>
                </svg>
                <span>Add Place</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

export default MyListsHeader;
